/**
 * Onboarding API client. The only operation today is marking the 3-step
 * tour complete for the current authenticated session.
 *
 * Retries up to twice on 5xx with exponential backoff so a transient
 * server blip doesn't permanently dismiss the tour while leaving the
 * server thinking it's still pending. 4xx responses (including 401)
 * reject immediately — they aren't transient.
 */

export type OnboardingTour = 'library' | 'edit';

export interface OnboardingCompleteResponse {
  tour: OnboardingTour;
  onboardingCompletedAt?: string;
  editOnboardingCompletedAt?: string;
}

const ENDPOINT = '/api/v1/auth/me/onboarding';
const RETRY_DELAYS_MS = [500, 1500] as const;

export async function markOnboardingComplete(
  options: { tour?: OnboardingTour } = {}
): Promise<OnboardingCompleteResponse> {
  const body = JSON.stringify({ tour: options.tour ?? 'library' });
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    let response: Response | null = null;
    try {
      response = await fetch(ENDPOINT, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body,
      });
    } catch (err) {
      // Network error — eligible for retry.
      lastError = err;
    }

    if (response) {
      if (response.ok) {
        return (await response.json()) as OnboardingCompleteResponse;
      }
      // 4xx is a permanent failure — don't retry.
      if (response.status < 500) {
        throw new Error(`markOnboardingComplete: ${response.status}`);
      }
      lastError = new Error(`markOnboardingComplete: ${response.status}`);
    }

    const delay = RETRY_DELAYS_MS[attempt];
    if (delay !== undefined) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error('markOnboardingComplete: failed after retries');
}
